import { z } from "zod";
import type { FieldDefinition } from "@noa/api-core";

export const SOURCE_TYPES = [
  { value: "blob", label: "Azure Blob Storage" },
  // Note: "onedrive" requires OneDriveExtractor registration - use SharePoint Sync tab instead
] as const;

export const PARSER_OPTIONS = [
  { value: "PDF", label: "PDF (Docling)" },
  { value: "PDF_FAST", label: "PDF Fast" },
  { value: "TXT", label: "Text" },
  { value: "MARKDOWN", label: "Markdown" },
] as const;

export const customJobSchema = z.object({
  sourceType: z.enum(["blob", "onedrive", "maximo", "local", "memory", "sharepoint"]),
  schemaId: z.string().nullable(),
  filesPattern: z.string().min(1),
  withVision: z.boolean(),
  skipExisting: z.boolean(),
  parser: z.enum(["PDF", "PDF_FAST", "DOCX", "HTML", "TXT", "MARKDOWN", "IMAGE", "DESCRIPTION"]).nullable(),
  limitEnabled: z.boolean(),
  maxRecords: z.number().min(1).max(10000),
});

export type CustomJobForm = z.infer<typeof customJobSchema>;

export const customJobDefaultValues: CustomJobForm = {
  sourceType: "blob",
  schemaId: null,
  filesPattern: "*",
  withVision: false,
  skipExisting: true,
  parser: null,
  limitEnabled: false,
  maxRecords: 10,
};

/** Whether a schema field's value comes from the file path/name rather than the LLM. */
export const isPathExtracted = (field: FieldDefinition): boolean => {
  const ext = field.extraction;
  if (!ext) return false;
  return !!(ext.from_path_pattern || ext.from_filename_pattern || ext.from_path_prefix || ext.from_metadata_key);
};

/** Human-readable description of where a path-extracted field's value comes from. */
export const getExtractionSource = (field: FieldDefinition): string | null => {
  const ext = field.extraction;
  if (!ext) return null;
  if (ext.from_path_pattern) return ext.from_path_pattern;
  if (ext.from_filename_pattern) return ext.from_filename_pattern;
  if (ext.from_path_prefix) return "path prefix";
  if (ext.from_metadata_key) return ext.from_metadata_key;
  return null;
};
